<script lang="ts" setup>
import { ChevronDownIcon } from "@frontiers/brink-icons";
import { OnClickOutside } from "@vueuse/components";
import type { IToolbarNavItemProps } from "./ToolbarNav.models";

const props = defineProps<IToolbarNavItemProps>();

const iconComponent = shallowRef<Component>(null);
const itemIcons = shallowRef<Record<string, Component>>({});
const isOpen = ref(false);

const loadIconComponent = async (icon: string) => {
  try {
    const iconModule = await import(`@frontiers/brink-icons`);
    return iconModule[icon as keyof typeof iconModule];
  } catch (error) {
    console.warn(`Failed to load icon: ${icon}`, error);
    return null;
  }
};

const getItemIcon = (iconName: string) => {
  return itemIcons.value[iconName] || null;
};

const closeMenu = () => {
  isOpen.value = false;
};

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

watch(
  () => props.icon,
  async (newIcon: string) => {
    if (newIcon) {
      iconComponent.value = await loadIconComponent(newIcon);
    }
  },
  { immediate: true }
);

watch(
  () => props.items,
  async (newItems: IToolbarNavItemProps[]) => {
    if (newItems) {
      for (const item of newItems) {
        if (item.icon && !itemIcons.value[item.icon]) {
          const component = await loadIconComponent(item.icon);
          if (component) {
            itemIcons.value[item.icon] = component;
          }
        }
      }
    }
  },
  { immediate: true, deep: true }
);
</script>
<template>
  <li class="ToolbarNavItem">
    <template v-if="onClick && !url && !items?.length">
      <button
        :data-event="
          dataEvent || $ga.getDataEventValue('ToolbarNav', 'button', label)
        "
        :data-research_topic_ids="dataResearchTopicIds"
        class="ToolbarNavItem__button"
        @click="onClick">
        <span class="ToolbarNavItem__icon">
          <component
            :is="iconComponent"
            v-if="iconComponent"
            size="lg" />
        </span>
        <p class="ToolbarNavItem__text">{{ label }}</p>
      </button>
    </template>
    <template v-else-if="url">
      <a
        :data-event="
          dataEvent || $ga.getDataEventValue('ToolbarNav', 'a', label)
        "
        :data-research_topic_ids="dataResearchTopicIds"
        :href="url"
        :target="target"
        class="ToolbarNavItem__link">
        <span class="ToolbarNavItem__icon">
          <component
            :is="iconComponent"
            v-if="iconComponent"
            size="lg" />
        </span>
        <p class="ToolbarNavItem__text">{{ label }}</p>
      </a>
    </template>
    <template v-if="items && items.length">
      <OnClickOutside @trigger="closeMenu">
        <button
          type="button"
          :data-event="$ga.getDataEventValue('ToolbarNav', 'button', label)"
          class="ToolbarNavItem__link ToolbarNavItem__link--menu"
          :aria-expanded="isOpen"
          aria-haspopup="menu"
          @click="toggleMenu">
          <span class="ToolbarNavItem__icon">
            <component
              :is="iconComponent"
              v-if="iconComponent"
              size="lg" />
          </span>
          <p class="ToolbarNavItem__text">
            {{ label }}
            <span
              class="ToolbarNavItem__chevron"
              :class="{ 'ToolbarNavItem__chevron--open': isOpen }">
              <ChevronDownIcon size="md" />
            </span>
          </p>
        </button>
        <ul
          role="menu"
          class="ToolbarNavItem__menu"
          :class="{ 'ToolbarNavItem__menu--open': isOpen }">
          <li
            v-for="item in items"
            :key="item.label"
            role="menuitem"
            class="ToolbarNavItem__menu__item">
            <component
              :is="item.onClick ? 'button' : 'a'"
              :href="item.onClick ? undefined : item.url"
              :target="item.onClick ? undefined : item.target"
              class="ToolbarNavItem__menu__link"
              :data-event="
                $ga.getDataEventValue(
                  'ToolbarNav',
                  item.onClick ? 'button' : 'a',
                  label
                )
              "
              @click="item.onClick ? item.onClick() : undefined">
              <component
                :is="getItemIcon(item.icon)"
                v-if="getItemIcon(item.icon)"
                size="md" />

              <span class="ToolbarNavItem__menu__text">
                {{ item.label }}
              </span>
            </component>
          </li>
          <slot name="custom-items"></slot>
        </ul>
      </OnClickOutside>
    </template>
  </li>
</template>
<style lang="scss">
.ToolbarNavItem {
  position: relative;
  display: grid;
  place-items: center;

  &__link,
  &__button {
    padding-top: 8px;
    padding-bottom: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: $textTertiary;

    @media #{$break-md} {
      padding-top: 0px;
      padding-bottom: 0px;
      min-width: 80px;
    }

    &:hover {
      color: $textPrimary;

      .ToolbarNavItem {
        &__icon {
          svg {
            fill: $textPrimary;
          }
        }

        &__text {
          color: $textPrimary;
        }

        &__chevron {
          background-color: $grey30;

          svg {
            fill: $textPrimary;
            background-color: $grey30;
          }
        }
      }
    }

    &--menu {
      &:hover {
        background-color: transparent;
      }
    }
  }

  &__icon {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;

    svg {
      fill: $textSecondary;
    }
  }

  &__text {
    @include f(button-sm);
    color: $textTertiary;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }

  &__chevron {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: $textTertiary;
    transition:
      transform 0.2s $bezier,
      background-color 0.2s $bezier;

    &--open {
      transform: rotate(180deg);
      background-color: $rgba-dark-8;
    }

    svg {
      fill: $textTertiary;
    }
  }

  &--primary {
    .ToolbarNavItem {
      &__icon {
        border-radius: 20px;
        background: $blue50;

        svg {
          fill: $white;
        }
      }

      &__text {
        color: $blue50;
      }
    }

    :hover {
      background: transparent;

      .ToolbarNavItem {
        &__icon {
          background: $blue70;

          svg {
            fill: $white;
          }
        }

        &__text {
          color: $blue70;
        }
      }
    }
  }

  &__menu {
    position: absolute;
    top: 100%;
    right: -12px;
    background-color: $white;
    border-radius: 8px;
    padding: 4px 0;
    border: 1px solid $grey70;
    display: flex;
    flex-direction: column;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.28s $bezier;
    box-shadow: $elevation-bottom-40;

    @media #{$break-md} {
      bottom: 100%;
      top: auto;
      right: 0;
    }

    &--open {
      pointer-events: all;
      opacity: 1;
    }

    &__item {
      button {
        width: 100%;
      }
    }

    &__link,
    &__button {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      padding: 16px;
      color: $textTertiary;

      &:hover {
        background-color: $grey10;

        &__text {
          color: $textPrimary;
        }

        svg {
          fill: $textPrimary;

          * {
            fill: $textPrimary;
          }
        }
      }

      svg {
        width: 16px;
        height: 16px;
        fill: $textTertiary;

        * {
          fill: $textTertiary;
        }
      }
    }

    &__text {
      @include f-16-300;
      color: $textTertiary;
      flex-grow: 1;
    }

    svg {
      flex-shrink: 0;
      fill: $textTertiary;
    }
  }
}
</style>
